import type { IAnyStateTreeNode, Instance } from "@gadgetinc/mobx-quick-tree";
import { expandBorderRadii, paddingUtil } from "@gadgetinc/themes/src/utils";
import { useStyletron } from "baseui";
import type { Override } from "baseui/overrides";
import { defaultTo, merge, omit } from "lodash";
import { observer } from "mobx-react-lite";
import type { PropsWithoutRef, ReactElement, RefAttributes } from "react";
import React, { forwardRef } from "react";
import { lightenCategoryColor } from "state-trees/src/AppCategoryColors";
import { useInteractionPreferences } from "../../../client-state-trees/workspace/InteractionPreferences";
import type { ButtonProps } from "../editor/Button";
import { Button, KIND, SIZE } from "../editor/Button";
import type { ToolTipLabelSize } from "../editor/ToolTip";
import { ToolTip } from "../editor/ToolTip";
import { SelectableButton } from "../editor/selectables/SelectableButton";
// eslint-disable-next-line no-restricted-imports
import type { ButtonOverrides } from "baseui/button";

interface IconButtonProps {
  color?: string;
  isInactive?: boolean;
  isVisible?: boolean;
  tooltipIgnoreBoundary?: boolean;
  tooltipEnabled?: boolean;
  label: string;
  labelSize?: ToolTipLabelSize;
  selectable?: boolean;
  node?: Instance<IAnyStateTreeNode>;
  subNodeKey?: string;
  overrides?: ButtonOverrides & { Wrapper?: Exclude<Override<any>, React.ComponentType<any>> };
  dataTestId?: string;
}

export const IconButton = observer(
  forwardRef<HTMLButtonElement, IconButtonProps & ButtonProps & PropsWithoutRef<ButtonProps> & RefAttributes<HTMLButtonElement>>(
    (props, ref): ReactElement => {
      const [css, $theme] = useStyletron();
      const preferences = useInteractionPreferences();
      const overlay = preferences?.inDarkMode ? "255, 255, 255" : "0, 0, 0";

      // Computes opacity difference between $theme.colors.primary50 & $theme.colors.primary100
      const hoverStyle = { backgroundColor: `rgba(${overlay}, ${1 - 0xee / 0xf6})` };

      // Computes opacity difference between $theme.colors.primary50 & $theme.colors.primary200
      const focusStyle = { backgroundColor: `rgba(${overlay}, ${1 - 0xe2 / 0xf6})` };

      const isVisible = props.isVisible ?? true;
      const defaultBackground = props.color ? lightenCategoryColor(props.color) : "transparent";
      const { onClick } = props;

      const overrides = merge(
        {
          BaseButton: {
            style: {
              ...expandBorderRadii($theme.borders.radius200),
              ...(props.size ? {} : paddingUtil("5px")),
              backgroundColor: props.isSelected ? focusStyle.backgroundColor : defaultBackground,
              color: $theme.colors.primaryA,
              height: props.size === "tight" ? $theme.sizing.scale700 : $theme.sizing.scale800,
              pointerEvents: props.isInactive ? "none" : "all",
              ":active": focusStyle,
              ":focus-visible": { ...focusStyle, outline: `auto 1px ${$theme.colors.borderFocus}`, outlineOffset: "1px" },
              ":hover": props.isSelected ? focusStyle : hoverStyle,
            },
          },
        },
        props.overrides,
        { Wrapper: undefined }
      );

      const buttonProps: ButtonProps = {
        onClick,
        overrides,
        kind: KIND.tertiary,
        ref,
        size: props.size ?? SIZE.compact,
        ...omit(props, [
          "isVisible",
          "tooltipIgnoreBoundary",
          "node",
          "subNodeKey",
          "onClick",
          "selectable",
          "tooltipEnabled",
          "dataTestId",
        ]),
      };

      let button = props.selectable ? (
        <SelectableButton
          node={props.node}
          subNodeKey={props.subNodeKey}
          onAction={() => {
            const handler = props.onClick as any as () => void;
            handler();
          }}
        >
          {(forwardProps) => <Button {...forwardProps} {...buttonProps} />}
        </SelectableButton>
      ) : (
        <Button {...buttonProps} />
      );

      if (defaultTo(props.tooltipEnabled, true)) {
        button = (
          <ToolTip label={props.label} labelSize={props.labelSize} ignoreBoundary={props.tooltipIgnoreBoundary}>
            {button}
          </ToolTip>
        );
      }

      return (
        <div
          data-testid={props.dataTestId}
          className={css({
            opacity: isVisible ? 1 : 0,
            pointerEvents: isVisible ? "all" : "none",
            ...props.overrides?.Wrapper?.style,
          })}
        >
          {button}
        </div>
      );
    }
  )
);
IconButton.displayName = "IconButton";
